import {
  Box,
  Button,
  DataTable,
  FlexBox,
  Icon,
  Page,
} from '@dixa/lib-shared-habla';
import { typography } from '@dixa/lib-shared-tokens';
import { Sentry } from 'lib-shared-sentry';

import { billingApi } from 'apis/billing';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { getCreditCardAttributes } from 'utils/billing';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { AddPaymentMethodModal } from './components/addPaymentMethodModal/addPaymentMethodModal';
import { CreditCardImage } from './components/creditCardImage';
import { isCreditCardExpired } from './utils/isCreditCardExpired';
import { useConfirmPaymentMethod } from './utils/useConfirmPaymentMethod';

export const BillingPaymentMethodsSection = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const { paymentMethods, isError, isFetching, refetch } =
    billingApi.useReadPaymentMethodsQuery(undefined, {
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        paymentMethods: data?.data.payment_methods,
      }),
    });

  const [
    updateDefaultPaymentMethod,
    {
      isLoading: isUpdateDefaultPaymentMethodLoading,
      originalArgs: updateDefaultPaymentMethodOriginalArgs,
    },
  ] = billingApi.useUpdateDefaultPaymentMethodMutation();

  const [
    deletePaymentMethodMutation,
    {
      isLoading: isDeletePaymentMethodLoading,
      originalArgs: deletePaymentMethodOriginalArgs,
    },
  ] = billingApi.useDeletePaymentMethodMutation();

  const [confirmPaymentMethod, { isLoading: isConfirmingPaymentMethod }] =
    useConfirmPaymentMethod();

  const openAddPaymentMethodModal = async () => {
    const { paymentId } = await dispatch(
      appActions.showModal({
        modal: { type: 'settings:addPaymentMethod', arg: {} },
      }),
    );

    if (paymentId) {
      requestConfirmPaymentMethod(paymentId);
      refetch();
    }
  };

  const handleOnDeletePaymentMethod = async (id: string) => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete card`,
            description: 'Are you sure you want to delete this card?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (!isConfirmed) {
      return;
    }

    try {
      await deletePaymentMethodMutation({ id });
      notify({
        title: 'Successfully deleted payment method',
        icon: 'done',
        className: 'success',
        time: 3000,
        showDesktop: false,
      });
    } catch (error) {
      notify({
        title: 'Failed to delete payment method',
        text: `Please try again or contact support.`,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
        showDesktop: false,
      });
    }
  };

  const handleOnUpdateDefaultPaymentMethod = async (id: string) => {
    try {
      await updateDefaultPaymentMethod({ id }).unwrap();
      refetch();
      notify({
        title: 'Successfully updated default payment method',
        icon: 'done',
        className: 'success',
        time: 3000,
        showDesktop: false,
      });
    } catch (error: any) {
      notify({
        title: 'Failed to update default payment method',
        text: `Please try again or contact support.`,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
        showDesktop: false,
      });
    }
  };

  const requestConfirmPaymentMethod = async (id: string) => {
    try {
      await confirmPaymentMethod(id);
      refetch();

      notify({
        title: 'Payment method successfully authorized',
        icon: 'done',
        className: 'success',
        time: 3000,
        showDesktop: false,
      });
    } catch (error: unknown) {
      Sentry.captureException(error);
      notify({
        title: 'Failed to authorize payment method',
        text: `Please try again or contact support.`,
        icon: 'exclamation',
        className: 'danger',
        time: 5000,
        showDesktop: false,
      });
    }
  };

  return (
    <Page.Section
      title="Payment methods"
      controls={
        <Button variant="primary" onClick={() => openAddPaymentMethodModal()}>
          Add payment method
        </Button>
      }
    >
      <DataTable
        tableStatusProps={{
          loading: isFetching,
          loadingFailed: isError,
          emptyState:
            !isFetching && !isError && !paymentMethods?.length
              ? {
                  title: 'There are no payment methods configured',
                  description: `Add a payment method for automatic payments, or contact support.`,
                }
              : undefined,
        }}
        data={paymentMethods ?? []}
        columns={[
          {
            label: 'Type',
            selector: (item) => (
              <FlexBox gap="300" alignItems="center">
                {item.is_default ? (
                  <Icon
                    name="done"
                    color={typography.color.success}
                    size={24}
                  />
                ) : (
                  <Box width="24px" />
                )}
                <CreditCardImage
                  width="40"
                  style={{
                    aspectRatio: '3 / 2',
                  }}
                  creditCard={item.cc_details.cc_type}
                />
                <span>{item.cc_details.cc_type}</span>
              </FlexBox>
            ),
            style: { width: '240px' },
          },
          {
            label: 'Card number',
            selector: (item) => (
              <Box>
                {`${
                  getCreditCardAttributes(item.cc_details.cc_type)
                    .anonymizedPattern
                } ${item.cc_details.cc_last_4}`}
              </Box>
            ),
            style: { width: '200px' },
          },
          {
            label: 'Expiration date',
            selector: (item) => (
              <Box
                color={
                  isCreditCardExpired(
                    Number(item.cc_details.cc_expiration_month),
                    Number(item.cc_details.cc_expiration_year),
                  )
                    ? typography.color.error
                    : typography.color.success
                }
              >
                {`${item.cc_details.cc_expiration_month} / ${item.cc_details.cc_expiration_year}`}
              </Box>
            ),
            style: { width: '140px' },
          },
          {
            label: 'Status',
            selector: (item) => (
              <Box
                as="span"
                color={
                  item.is_authorized
                    ? typography.color.success
                    : typography.color.error
                }
              >
                {item.is_authorized ? 'Authorized' : 'Needs authorization'}
              </Box>
            ),
            style: { width: 'auto' },
          },
          {
            label: '',
            style: { width: '0px' },
            selector: (item) => (
              <FlexBox gap="200" justifyContent="flex-end">
                {!item.is_authorized && (
                  <Button
                    variant="secondary"
                    onClick={() =>
                      requestConfirmPaymentMethod(item.external_key)
                    }
                    isLoading={isConfirmingPaymentMethod}
                  >
                    Authorize card
                  </Button>
                )}

                {!item.is_default && (
                  <Button
                    variant="secondary"
                    onClick={() => handleOnUpdateDefaultPaymentMethod(item.id)}
                    isLoading={
                      isUpdateDefaultPaymentMethodLoading &&
                      updateDefaultPaymentMethodOriginalArgs?.id === item.id
                    }
                  >
                    Use as default card
                  </Button>
                )}

                <Button
                  variant="secondary"
                  onClick={() => handleOnDeletePaymentMethod(item.id)}
                  isLoading={
                    isDeletePaymentMethodLoading &&
                    deletePaymentMethodOriginalArgs?.id === item.id
                  }
                >
                  Delete card
                </Button>
              </FlexBox>
            ),
          },
        ]}
      />

      <AddPaymentMethodModal />
    </Page.Section>
  );
};
